import AppConfig from '@/AppConfig';
import plumLogo from '@/assets/plum.svg';
import useConfirm from '@/hooks/useConfirm';
import {
  accountDetailsQuery,
  authenticateMeQuery,
  logoutFromAllDevicesQuery,
} from '@/queries/auth.queries';
import { getPlumAppUrl, redirectToPlumApp, removeJwtToken } from '@/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  AccountSwitch,
  AddProfile,
  Dashboard,
  DocumentOutlined,
  HeartOutlined,
  LogoutOutlined,
  Profile,
} from 'ui/icons';
import NavigationDropdown, {
  INavigationDropdownSections,
} from './NavigationDropdown';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { useAppContext } from '@/context/AppContext';

interface IProps {
  className?: string;
  sticky?: boolean;
  bg?: string;
}

const Navbar = ({ className, sticky, bg }: IProps) => {
  const navigate = useNavigate();

  const { isWebView } = useAppContext();

  const { data } = useQuery({
    ...authenticateMeQuery(),
  });

  const { data: accountDetails } = useQuery({
    ...accountDetailsQuery(),
    enabled: !isWebView,
  });
  
  const { mutate } = useMutation({
    ...logoutFromAllDevicesQuery(),
  });

  const { triggerConfirm, Alert } = useConfirm();

  const navSections: INavigationDropdownSections = [
    {
      sectionHeading: 'PERSONAL',
      items: [
        {
          title: 'My benefits',
          action: redirectToPlumApp('/benefits-revamp'),
          icon: <HeartOutlined />,
        },
        {
          title: 'My claims',
          action: () => navigate(AppConfig.HOME_PAGE),
          //     sendEmployeeAppEvent(
          //       TRACKER_EVENTS.CLAIMS_MENU_OPTION_CLICKED
          //     );
          icon: <DocumentOutlined />,
        },
        ...(data?.isMidTermAdditionEnabled
          ? [
              {
                title: 'Add family to policy',
                action: () => navigate('/family-addition/add-family-member'),
                // sendEmployeeAppEvent(
                //   TRACKER_EVENTS.CLAIMS_MENU_OPTION_CLICKED
                // );
                icon: <AddProfile height={24} width={24} />,
              },
            ]
          : []),
        {
          title: 'Profile',
          action: redirectToPlumApp('/profile'),
          icon: <Profile />,
        },
        ...(accountDetails && accountDetails.length >= 2
          ? [
              {
                title: 'Switch Accounts',
                icon: <AccountSwitch />,
                action: redirectToPlumApp(`/benefits-revamp?acs=true`),
              },
            ]
          : []),
      ],
    },
    {
      sectionHeading: '',
      items: [
        {
          title: 'Log out all devices',
          action: async () => {
            const isConfirmed = await triggerConfirm({
              title: 'Are you sure?',
              description:
                'Are you sure you want to logout from all devices (including the current device)?',
              cancelButtonText: 'No',
              confirmButtonText: 'Yes, Logout',
            });
            if (isConfirmed) {
              mutate(
                {},
                {
                  onSuccess: () => {
                    removeJwtToken();
                    redirectToPlumApp('/logout')();
                  },
                  onError: () => {
                    toast.error('Failed to logout. Please try again later.');
                  },
                },
              );
            }
          },
          icon: <LogoutOutlined fill="#55657D" />,
        },
        {
          title: 'Log out',
          action: () => {
            window.dev.i('LOGOUT: Logging out');
            removeJwtToken();
            window.dev.i(
              'LOGOUT: JWT Token removed',
              Cookies.get(AppConfig.JWT_TOKEN_COOKIE_NAME),
            );
            redirectToPlumApp('/logout')();
          },
          icon: <LogoutOutlined />,
          color: 'primary-500',
        },
      ],
    },
  ];

  if (data?.admin) {
    navSections.unshift({
      sectionHeading: 'ADMIN',
      items: [
        {
          title: 'Admin dashboard',
          action: redirectToPlumApp('/dashboard'),
          icon: <Dashboard />,
        },
      ],
    });
  }
  return (
    // pl-1 b/c plum logo has a padding of 16px inside it
    <nav
      id="claims-navbar"
      className={twMerge(
        clsx({
          'border-b border-gray-100 pl-1 pr-5 lg:px-0 flex items-center justify-center h-[96px]':
            true,
          'sticky top-0 z-20': sticky,
          [`${bg}`]: bg,
        }),
        className,
      )}
    >
      <Alert />
      <div
        className={`flex items-center justify-between max-w-[1120px] w-full`}
      >
        <Link to={getPlumAppUrl('/benefits-revamp')}>
          <img src={plumLogo} width="128px" height="64px" alt="plum" />
        </Link>
        <NavigationDropdown sections={navSections} />
      </div>
    </nav>
  );
};

export default Navbar;
